import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, ConfirmDialog, Flex, Menu, MenuItem, Popover, Text } from 'core/components';
import { FiMoreVertical } from 'react-icons/fi';
import { TbX, TbTrash, TbArrowBarToDown } from 'react-icons/tb';
import type { QueryHistoryEntry } from '@kentik/ui-shared/boards/panels';
import Markdown from 'app/views/core/journeys/Markdown';

interface UdeQueryHistoryListProps {
  entries: QueryHistoryEntry[];
  headIndex: number;
  onSelectEntry: (index: number) => void;
  onRemoveEntry: (index: number) => void;
  onSetToHead: (index: number) => void;
  onClose: () => void;
  hideHeader?: boolean;
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return 'Just now';
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    return `${diffHour}h ago`;
  }

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) {
    return 'Yesterday';
  }
  if (diffDay < 7) {
    return `${diffDay}d ago`;
  }

  return new Date(timestamp).toLocaleDateString();
}

const HistoryContainer = styled(Flex)`
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: ${({ theme }) => `${theme.borders.thin}`};
`;

const ScrollArea = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
`;

const EntryCard = styled(Flex)<{ $isActive: boolean }>`
  flex-direction: column;
  padding: 8px 10px;
  margin-bottom: 6px;
  border-radius: 4px;
  cursor: pointer;
  border-left: 3px solid ${({ $isActive, theme }) => ($isActive ? theme.colors.primary : 'transparent')};
  background: ${({ $isActive, theme }) => ($isActive ? theme.colors.subnavBackground : 'transparent')};

  code {
    font-size: 11px;
  }

  p {
    font-size: 12px;
    line-height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.subnavBackground};
  }
`;

const Timestamp = styled(Text)`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.muted};
`;

const PromptLabel = styled(Text)`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.muted};
  font-style: italic;
  margin-top: 2px;
`;

const OverflowButton = styled(Button)`
  opacity: 0.4;
  &:hover {
    opacity: 1;
  }
`;

function UdeQueryHistoryList({
  entries,
  headIndex,
  onSelectEntry,
  onRemoveEntry,
  onSetToHead,
  onClose,
  hideHeader = false
}: UdeQueryHistoryListProps) {
  const [confirmAction, setConfirmAction] = useState<{ type: 'remove' | 'setToHead'; index: number } | null>(null);

  const handleConfirm = useCallback(() => {
    if (!confirmAction) {
      return;
    }
    if (confirmAction.type === 'remove') {
      onRemoveEntry(confirmAction.index);
    } else {
      onSetToHead(confirmAction.index);
    }
    setConfirmAction(null);
  }, [confirmAction, onRemoveEntry, onSetToHead]);

  const handleCancel = useCallback(() => {
    setConfirmAction(null);
  }, []);
  if (entries.length === 0) {
    return (
      <HistoryContainer>
        {!hideHeader && (
          <Header>
            <Text fontWeight="bold" fontSize={13}>
              Query History
            </Text>
            <Button small minimal icon={TbX} onClick={onClose} />
          </Header>
        )}
        <Flex flex={1} alignItems="center" justifyContent="center" p="24px">
          <Text color="muted" fontSize={12} textAlign="center">
            No history yet. Run a query to start tracking changes.
          </Text>
        </Flex>
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer>
      {!hideHeader && (
        <Header>
          <Text fontWeight="bold" fontSize={13}>
            Query History
          </Text>
          <Button small minimal icon="cross" onClick={onClose} />
        </Header>
      )}
      <ScrollArea>
        {entries.map((entry, index) => (
          <EntryCard key={entry.timestamp} $isActive={index === headIndex} onClick={() => onSelectEntry(index)}>
            <Flex justifyContent="space-between" alignItems="flex-start">
              <Box flex={1} minWidth={0}>
                <Markdown>{entry.summary}</Markdown>
                <Timestamp>{formatRelativeTime(entry.timestamp)}</Timestamp>
                {entry.user_prompt && <PromptLabel>&ldquo;{entry.user_prompt}&rdquo;</PromptLabel>}
              </Box>
              <span onClick={(e) => e.stopPropagation()} role="presentation">
                <Popover position="bottom-right">
                  <OverflowButton icon={FiMoreVertical} minimal small />
                  <Menu minWidth={160}>
                    <MenuItem
                      icon={TbTrash}
                      text="Remove"
                      intent="danger"
                      disabled={index === headIndex}
                      onClick={() => setConfirmAction({ type: 'remove', index })}
                    />
                    <MenuItem
                      icon={TbArrowBarToDown}
                      text="Set to Head"
                      disabled={index === 0}
                      onClick={() => setConfirmAction({ type: 'setToHead', index })}
                    />
                  </Menu>
                </Popover>
              </span>
            </Flex>
          </EntryCard>
        ))}
      </ScrollArea>

      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'remove' ? 'Remove History Entry?' : 'Set to Head?'}
        confirmButtonText={confirmAction?.type === 'remove' ? 'Remove' : 'Set to Head'}
        confirmButtonIntent={confirmAction?.type === 'remove' ? 'danger' : 'primary'}
        text={
          confirmAction?.type === 'remove'
            ? 'This history entry will be permanently removed.'
            : 'All history entries newer than this one will be permanently removed, and this entry will become the current query.'
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </HistoryContainer>
  );
}

export default UdeQueryHistoryList;
